import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Download } from "lucide-react";

import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import TablePagination from "@/components/common/TablePagination";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CommonModal from "@/components/common/CommonModal";
import {
  DocumentPreview,
  type TDocumentInput,
} from "@/utility/documentPreview";

import { PayslipService } from "@/services/Payslip/PayslipService";
import {
  showToastNotification,
  handleFormatAmount,
  getCurrentPeriod,
} from "@/utility/helper";
import { ThrowError } from "@/api/callApi";
import type { IPageInfo, IFilter } from "@/interface/common.interface";
import type {
  IGertUserListResponse,
  IGetUserList,
} from "@/interface/payslip.interface";

// Pay-cycle config
// cycle runs day-21 -> day-20 of next month (e.g. 2026-07-21 to 2026-08-20).
// Change this single constant if the cycle start day ever changes.
const CYCLE_START_DAY = 21;
const MIN_SELECTABLE_YEAR = 1990;
const MAX_SELECTABLE_YEAR = new Date().getFullYear() + 1;

const formatDateISO = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const generateMonthOptionsForYear = (
  year: number,
  cycleStartDay: number = CYCLE_START_DAY,
) => {
  const options: { label: string; startDate: string; endDate: string }[] = [];
  for (let m = 0; m < 12; m++) {
    const start = new Date(year, m, cycleStartDay);
    const end = new Date(year, m + 1, cycleStartDay - 1);
    options.push({
      label: `${start.toLocaleString("default", { month: "short" })} ${start.getFullYear()} - ${end.toLocaleString("default", { month: "short" })} ${end.getFullYear()}`,
      startDate: formatDateISO(start),
      endDate: formatDateISO(end),
    });
  }
  return options;
};

const BranchUsersPayslip: React.FC = () => {
  const { id: branchId } = useParams<{ id: string }>();
  const [users, setUsers] = useState<IGetUserList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<TDocumentInput>(null);
  const [showPreview, setShowPreview] = useState(false);

  const [filter, setFilter] = useState<IFilter>({
    startDate: getCurrentPeriod().startDate,
    endDate: getCurrentPeriod().endDate,
  });

  const [selectedYear, setSelectedYear] = useState<number>(() =>
    filter.startDate ? new Date(filter.startDate).getFullYear() : new Date().getFullYear(),
  );

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const monthOptions = useMemo(() => generateMonthOptionsForYear(selectedYear), [selectedYear]);

  const selectedPeriodLabel = useMemo(() => {
    const option = monthOptions.find(
      (opt) =>
        opt.startDate === filter.startDate && opt.endDate === filter.endDate,
    );
    return option ? option.label : "Custom";
  }, [filter.startDate, filter.endDate, monthOptions]);

 const applyPeriod = (startDate: string, endDate: string) => {
  setFilter((prev) => ({
    ...prev,
    startDate,
    endDate,
  }));

  setPaginationData((prev) => ({
    ...prev,
    currentPage: 1,
  }));
};

  const handleYearChange = (value: string) => {
    const year = Number(value);
    if (!value || Number.isNaN(year)) return;
    if (year < MIN_SELECTABLE_YEAR || year > MAX_SELECTABLE_YEAR) return;

    const currentIdx = monthOptions.findIndex(
      (opt) => opt.startDate === filter.startDate && opt.endDate === filter.endDate,
    );
    setSelectedYear(year);
    const periods = generateMonthOptionsForYear(year);
    const next = (currentIdx >= 0 && periods[currentIdx]) || periods[0];
    applyPeriod(next.startDate, next.endDate);
  };

  const handlePeriodChange = (value: string) => {
    const option = monthOptions.find((opt) => opt.label === value);
    if (option) {
      applyPeriod(option.startDate, option.endDate);
    }
  };

  const goToCurrentPeriod = () => {
    const period = getCurrentPeriod();
    setSelectedYear(new Date(period.startDate).getFullYear());
    applyPeriod(period.startDate, period.endDate);
  };

  const handleChangePageSize = (size: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const fetchUsers = useCallback(async () => {
    if (!branchId) {
      showToastNotification("danger", "No branch ID provided in UR");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        branchId,
        startDate: filter.startDate || "",
        endDate: filter.endDate || "",
        pageNumber: String(paginationData.currentPage),
        pageSize: String(paginationData.pageSize),
      }).toString();

      const response = (await PayslipService.getList(
        params,
      )) as IGertUserListResponse;

      if (response?.status) {
        setUsers(response.data.users || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response.data.pageInfo?.total || 0,
          totalPages: response.data.pageInfo?.totalPages || 0,
        }));
      }
    } catch (error) {
      ThrowError(error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [
    branchId,
    filter.startDate,
    filter.endDate,
    paginationData.currentPage,
    paginationData.pageSize,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleGeneratePayslip = async (userId: string) => {
    if (!filter.startDate || !filter.endDate) {
      showToastNotification("danger", "Please select a valid salary period.");
      return;
    }

    try {
      setGenerating(userId);
      const arrayBuffer = await PayslipService.generatePayslip(
        userId,
        filter.startDate,
        filter.endDate,
      );

      if (!arrayBuffer) {
        showToastNotification("danger", "Failed to generate payslip.");
        return;
      }

      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      const file = new File(
        [blob],
        `payslip_${userId}_${filter.startDate}_${filter.endDate}.pdf`,
        { type: "application/pdf" },
      );

      setSelectedDoc(file);
      setShowPreview(true);
      showToastNotification("success", "Payslip generated successfully!");
    } catch (error) {
      ThrowError(error);
    } finally {
      setGenerating(null);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setSelectedDoc(null);
  };

  const columns: TableColumn<IGetUserList>[] = [
    {
      key: "employeeCode",
      header: "Employee Code",
      render: (row) => (
        <span className="font-mono text-sm">{row.employeeCode}</span>
      ),
    },
    {
      key: "employee",
      header: "Employee",
      render: (row) => <span className="font-medium">{row.employee}</span>,
    },
    {
      key: "email",
      header: "Email",
      render: (row) => <span className="text-sm">{row.email}</span>,
    },
    {
      key: "phone",
      header: "Phone",
      render: (row) => <span className="text-sm">{row.phone}</span>,
    },
    {
      key: "basicPay",
      header: "Basic Pay",
      render: (row) => (
        <span className="font-medium">{handleFormatAmount(row.basicPay)}</span>
      ),
    },
    {
      key: "grossPay",
      header: "Gross Pay",
      render: (row) => (
        <span className="font-medium text-emerald-700">
          {handleFormatAmount(row.grossPay)}
        </span>
      ),
    },
    {
      key: "deduction",
      header: "Deduction",
      render: (row) => (
        <span className="font-medium text-rose-700">
          {handleFormatAmount(row.deduction)}
        </span>
      ),
    },
    {
      key: "netPay",
      header: "Net Pay",
      render: (row) => (
        <span className="font-bold text-primary">
          {handleFormatAmount(row.netPay)}
        </span>
      ),
    },
    {
      key: "isSalaryCreated",
      header: "Salary Created",
      render: (row) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.isSalaryCreated
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-600"
            }`}
        >
          {row.isSalaryCreated ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleGeneratePayslip(row.userId)}
          disabled={!row.isSalaryCreated || generating === row.userId}
          title={!row.isSalaryCreated ? "Salary not created for this user" : ""}
          className="flex items-center gap-1.5"
        >
          {generating === row.userId ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Generate
        </Button>
      ),
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading title="Branch Users Payslip" isBackButtonVisible={true} />

        <Card className="border-0 shadow-md p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-slate-600 whitespace-nowrap">
                Salary Period:
              </span>

              <input
                type="number"
                inputMode="numeric"
                value={selectedYear}
                min={MIN_SELECTABLE_YEAR}
                max={MAX_SELECTABLE_YEAR}
                onChange={(e) => handleYearChange(e.target.value)}
                className="w-[90px] border border-slate-200 rounded-md px-3 py-2 text-sm h-9 focus:outline-none focus:ring-2 focus:ring-primary/30"
                aria-label="Salary year"
              />

              <Select
                value={selectedPeriodLabel}
                onValueChange={handlePeriodChange}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((opt) => (
                    <SelectItem key={opt.label} value={opt.label}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={goToCurrentPeriod}
                className="flex items-center gap-1.5"
              >
                Today
              </Button>
            </div>
          </div>
        </Card>

        {/* Users Table */}
        <Card className="border-0 shadow-md">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Users in Branch</h3>
            </div>
            <span className="text-sm text-slate-500">
              Total: {paginationData.total} users
            </span>
          </div>

          <CommonTable
            columns={columns}
            data={users}
            loading={loading}
            emptyMessage="No users found for this branch in the selected period."
          />

          <TablePagination
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            handleChangePageSize={handleChangePageSize}
            isLoading={loading}
          />
        </Card>
      </div>

      <CommonModal
        title="Payslip Preview"
        show={showPreview}
        onClose={handleClosePreview}
        className="sm:max-w-3xl"
      >
        <DocumentPreview selectedDocument={selectedDoc} />
      </CommonModal>
    </Layout>
  );
};

export default BranchUsersPayslip;